import { useState, useEffect } from 'react';
import { motion } from 'motion/react';

interface AITypingEffectProps {
  text: string;
  speed?: number; // ms per character
  delay?: number; // initial delay before typing starts
  onComplete?: () => void;
  className?: string;
  showCursor?: boolean;
}

/**
 * Typewriter effect for AI-generated content.
 * Creates the illusion of real-time AI text generation.
 */
export function AITypingEffect({
  text,
  speed = 30,
  delay = 0,
  onComplete,
  className = '',
  showCursor = true,
}: AITypingEffectProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    // Reset when text changes
    setDisplayedText('');
    setIsTyping(false);
    setHasStarted(false);

    // Initial delay before typing starts
    const delayTimeout = setTimeout(() => {
      setHasStarted(true);
      setIsTyping(true);
    }, delay);

    return () => clearTimeout(delayTimeout);
  }, [text, delay]);

  useEffect(() => {
    if (!hasStarted || !isTyping) return;

    if (displayedText.length < text.length) {
      const timeout = setTimeout(() => {
        // Add some variation to typing speed for realism
        const variation = Math.random() * 20 - 10;
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, speed + (Math.random() * 20 - 10));

      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
      onComplete?.();
    }
  }, [displayedText, text, speed, hasStarted, isTyping, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {showCursor && isTyping && (
        <motion.span
          className="inline-block w-0.5 h-5 bg-amber-400 ml-0.5 align-middle"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      )}
    </span>
  );
}

interface AITypingParagraphProps {
  text: string;
  speed?: number;
  delay?: number;
  onComplete?: () => void;
  className?: string;
}

/**
 * Multi-line typing effect that respects paragraph structure.
 */
export function AITypingParagraph({
  text,
  speed = 25,
  delay = 0,
  onComplete,
  className = '',
}: AITypingParagraphProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let currentIndex = 0;
      
      const typeInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
          setIsComplete(true);
          onComplete?.();
        }
      }, speed);

      return () => clearInterval(typeInterval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay, onComplete]);

  return (
    <p className={className}>
      {displayedText}
      {!isComplete && (
        <motion.span
          className="inline-block w-0.5 h-4 bg-amber-400 ml-0.5 align-middle"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      )}
    </p>
  );
}

interface StreamingTextProps {
  text: string;
  isActive: boolean;
  speed?: number;
  className?: string;
}

/**
 * Text that streams in when activated, or shows fully when not.
 * Useful for content that should animate only the first time it's revealed.
 */
export function StreamingText({
  text,
  isActive,
  speed = 20,
  className = '',
}: StreamingTextProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isActive && !hasAnimated) {
      let currentIndex = 0;
      
      const typeInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
          setHasAnimated(true);
        }
      }, speed);

      return () => clearInterval(typeInterval);
    } else if (hasAnimated || !isActive) {
      setDisplayedText(text);
    }
  }, [text, isActive, hasAnimated, speed]);

  // Reset if text changes
  useEffect(() => {
    setDisplayedText('');
    setHasAnimated(false);
  }, [text]);

  const showCursor = isActive && !hasAnimated && displayedText.length < text.length;

  return (
    <span className={className}>
      {displayedText}
      {showCursor && (
        <motion.span
          className="inline-block w-0.5 h-4 bg-amber-400 ml-0.5 align-middle"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      )}
    </span>
  );
}
